import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import omit from 'lodash/omit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import * as Yup from 'yup'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import FormFrame from 'modules/Forms/Account/Create/subcomponents/FormFrame'

export const YandexZenSchema = () =>
  Yup.object()
    .shape({
      logins: Yup.array().of(Yup.object()),
      login: Yup.string().when('logins', ([logins], schema) =>
        logins
          ? schema.strip()
          : schema
              .test('baseLoginTest', '', baseLoginValidator)
              .required('common::validation_messages.required'),
      ),
      network: Yup.string().required('common::validation_messages.required'),
      email: Yup.string()
        .test(
          'emailValidator',
          'common::validation_messages.incorrect_email_format',
          emailValidator,
        )
        .required('common::validation_messages.required'),
      lastname: Yup.string().required('common::validation_messages.required'),
      firstname: Yup.string().required('common::validation_messages.required'),
      promo_page_name: Yup.string().required('common::validation_messages.required'),
      promo_page_url: Yup.string().required('common::validation_messages.required'),
    })
    .concat(advertiserInfoValidationRulesExtended())

let CreateYandexZenAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
  submitFailed,
}) => {
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  const {
    formValues: { email, advertiser_info },
    user,
  } = useSelector((state) => ({
    formValues: selector(state, 'email', 'advertiser_info'),
    user: state['app'].user,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['advertiser_template'])),
    [submitAccountData],
  )

  return (
    <FormFrame
      onSubmit={handleSubmit(submit)}
      multiLogins={multiLogins}
      form={form}
      advertiser_info={advertiser_info}
      submitFailed={submitFailed}
      submitAccountData={submitAccountData}
      submitting={submitting}
      onClose={onClose}
    >
      <Field
        disabled
        name="network"
        hidden
        fullWidth
        component={renderInputField}
        t={t}
      />

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <LoginField
          form={form}
          multiLogins={multiLogins}
          array={array}
          autofill={autofill}
          networkId={networkId}
          label={'services::forms.register_account.yandex.fields.login.label'}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="promo_page_name"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.yandex_zen.fields.promo_page_name.label')}
          t={t}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="promo_page_url"
          type="url"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.yandex_zen.fields.promo_page_url.label')}
          t={t}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="email"
          type="email"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.yandex_zen.fields.email.label')}
          t={t}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="firstname"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.yandex_zen.fields.firstname.label')}
          t={t}
        />
      </div>

      <div className={clsx('margin-bottom-48', formStyles['row_with-tooltip'])}>
        <Field
          name="lastname"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.yandex_zen.fields.lastname.label')}
          t={t}
        />
      </div>
    </FormFrame>
  )
}

CreateYandexZenAccountForm = reduxForm({
  form: 'CreateYandexZenAccountForm',
  initialValues: {
    login: '',
    network: '',
    email: '',
    lastname: '',
    firstname: '',
    promo_page_name: '',
    promo_page_url: '',
    payment_profile_id: undefined,
    advertiser_info: initialAdvertiserInfo,
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateYandexZenAccountForm)

CreateYandexZenAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  change: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  submitFailed: PropTypes.bool,
}

export default CreateYandexZenAccountForm
